import { ElementType, forwardRef, useId } from "react";

import { FormHelperText, InputBaseComponentProps, Button } from "@mui/material";
import { gray } from "configs/colors";
import InputNumber, { InputNumberProps } from "rc-input-number";
import { rem } from "utils";

import { useNumberField } from "./NumberField.hooks";
import {
  AddonWrapper,
  Container,
  Input,
  InputWrapper,
} from "./NumberField.styles";
import { NumberFieldProps, NumberFieldReference } from "./NumberField.types";
import { MinusIcon, PlusIcon } from "../Icon";
import InputLabel from "../InputLabel/InputLabel";

interface CustomProps {
  onChange: (event: {
    target: { name: string; value: string | number };
  }) => void;
  name: string;
}

const NumericFormatCustom = forwardRef<InputNumberProps, CustomProps>(
  function NumericFormatCustom(props) {
    const { onChange, ...other } = props;

    return (
      <InputNumber
        {...other}
        onChange={(values) => {
          onChange({
            target: {
              name: props.name,
              value: String(values ?? ""),
            },
          });
        }}
      />
    );
  }
);

const NumberField = forwardRef<NumberFieldReference, NumberFieldProps>(
  (
    {
      label,
      id,
      defaultValue,
      value,
      type,
      inputNumberProps,
      controls,
      readOnly,
      fullWidth = true,
      InputProps = {},
      addonBefore,
      addonBeforeProps,
      addonAfter,
      addonAfterProps,
      helperText,
      onChange,
      ...innerProps
    },
    ref
  ) => {
    const {
      numberfieldRef,
      valueState,
      formatter,
      parser,
      onIncreaseHandler,
      onDecreaseHandler,
      onChangeHandler,
    } = useNumberField({
      ref,
      defaultValue,
      type,
      value,
      onChange,
    });
    const formId = useId();
    return (
      <Container>
        {!!label && (
          <InputLabel htmlFor={id ?? formId} required={innerProps.required}>
            {label}
          </InputLabel>
        )}
        <InputWrapper color={innerProps.color} fullWidth={fullWidth}>
          {(controls || addonBefore) && (
            <AddonWrapper
              color="inherit"
              placement="before"
              as={controls ? Button : undefined}
              disabled={
                innerProps.disabled ||
                readOnly ||
                (controls && typeof inputNumberProps?.min === "number"
                  ? Number(valueState) <= Number(inputNumberProps?.min ?? 0)
                  : false)
              }
              onClick={onDecreaseHandler}
              {...addonBeforeProps}
            >
              {addonBefore ?? (
                <MinusIcon
                  sx={{ color: gray[700], width: rem(18), height: rem(18) }}
                />
              )}
            </AddonWrapper>
          )}
          <Input
            id={id ?? formId}
            withAddonBefore={!!addonBefore}
            withAddonAfter={!!addonAfter}
            variant="outlined"
            size="small"
            type={type}
            ref={numberfieldRef}
            noBorder={controls}
            fullWidth={fullWidth}
            value={valueState}
            onChange={onChangeHandler}
            InputProps={{
              inputComponent:
                NumericFormatCustom as unknown as ElementType<InputBaseComponentProps>,
              readOnly,
              ...InputProps,
              inputProps: {
                ...(type && { formatter }),
                ...(type && { parser }),
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ...(inputNumberProps as any),
                ...InputProps?.inputProps,
              },
            }}
            {...innerProps}
          />
          {(controls || addonAfter) && (
            <AddonWrapper
              color="inherit"
              placement="after"
              as={controls ? Button : undefined}
              disabled={
                innerProps.disabled ||
                readOnly ||
                (controls && typeof inputNumberProps?.max === "number"
                  ? Number(valueState) >= Number(inputNumberProps?.max ?? 0)
                  : false)
              }
              onClick={controls ? onIncreaseHandler : undefined}
              {...addonAfterProps}
            >
              {addonAfter ?? (
                <PlusIcon
                  sx={{ color: gray[700], width: rem(18), height: rem(18) }}
                />
              )}
            </AddonWrapper>
          )}
        </InputWrapper>
        {helperText ? (
          <FormHelperText
            variant="outlined"
            required={innerProps.required}
            error={innerProps.error}
            disabled={innerProps.disabled}
          >
            {helperText}
          </FormHelperText>
        ) : null}
      </Container>
    );
  }
);

export default NumberField;
